import { useEffect, useState } from 'react'
import { writeCheckoutGuestCartId } from '../../cart/cartPersistence'
import { useCustomerAuth } from '../../customer-auth'
import { useShopI18n } from '../../i18n'
import { CartErrorBanner } from './cart-page/CartErrorBanner'
import { CartItemsSection } from './cart-page/CartItemsSection'
import { CartPageHeader } from './cart-page/CartPageHeader'
import { CartSidebarSummary } from './cart-page/CartSidebarSummary'
import { CartTotalsSection } from './cart-page/CartTotalsSection'
import { useCartPageModel } from './cart-page/useCartPageModel'

const CartPage = () => {
  const { formatCurrency, t, toLocalizedPath } = useShopI18n()
  const { authenticated } = useCustomerAuth()
  const {
    cartId,
    itemCount,
    subtotal,
    busy,
    initializing,
    error,
    clearError,
    sortedItems,
    clearCart,
    handleIncrease,
    handleDecrease,
    handleRemove,
    isItemBusy,
  } = useCartPageModel()
  const [autoCheckoutAttempted, setAutoCheckoutAttempted] = useState(false)
  const checkoutPath = toLocalizedPath('/checkout')
  const loginRedirectPath = `${toLocalizedPath('/dashboard/login')}?intent=checkout&next=${encodeURIComponent(checkoutPath)}`
  const resolvedError = error
  const checkoutCtaLabel = t('cart.checkout')

  const handleCheckout = () => {
    if (!authenticated) {
      if (cartId) writeCheckoutGuestCartId(cartId)
      window.location.assign(loginRedirectPath)
      return
    }
    if (!cartId) return
    window.location.assign(checkoutPath)
  }

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (autoCheckoutAttempted) return
    if (initializing || itemCount <= 0) return

    const params = new URLSearchParams(window.location.search)
    if (params.get('checkout') !== '1') return

    setAutoCheckoutAttempted(true)
    params.delete('checkout')
    const query = params.toString()
    const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ''}`
    window.history.replaceState(window.history.state, '', cleanUrl)
    handleCheckout()
  }, [autoCheckoutAttempted, authenticated, cartId, checkoutPath, initializing, itemCount, loginRedirectPath])

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_12%_8%,#dbf4ff,transparent_32%),radial-gradient(circle_at_90%_5%,#d9fce8,transparent_28%),linear-gradient(180deg,#f8fbff_0%,#eef3f8_58%,#e8eef6_100%)]" />
      <div className="shop-layout space-y-6 py-8">
        <CartPageHeader
          title={t('cart.title')}
          preCheckout={t('cart.preCheckout')}
          continueShoppingLabel={t('cart.continueShopping')}
          clearLabel={t('cart.clear')}
          toLocalizedPath={toLocalizedPath}
          clearDisabled={busy || itemCount === 0}
          onClear={() => {
            void clearCart()
          }}
        />

        <CartErrorBanner
          error={resolvedError}
          closeLabel={t('common.close')}
          actionLabel={undefined}
          onAction={undefined}
          onClose={() => {
            clearError()
          }}
        />

        {initializing && (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
            {t('cart.loading')}
          </div>
        )}

        {!initializing && sortedItems.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white px-6 py-8 text-center shadow-sm">
            <p className="text-lg font-semibold text-slate-900">{t('cart.emptyTitle')}</p>
            <p className="mt-2 text-sm text-slate-600">{t('cart.emptyDescription')}</p>
          </div>
        )}

        {!initializing && sortedItems.length > 0 && (
          <div className="grid gap-4 xl:grid-cols-[1.35fr_0.65fr]">
            <CartItemsSection
              items={sortedItems}
              removeLabel={t('cart.remove')}
              unitPriceLabel={(price) => t('cart.unitPrice', { price })}
              formatCurrency={formatCurrency}
              isItemBusy={isItemBusy}
              onIncrease={(item) => {
                void handleIncrease(item)
              }}
              onDecrease={(item) => {
                void handleDecrease(item)
              }}
              onRemove={(item) => {
                void handleRemove(item)
              }}
            />

            <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
              <CartSidebarSummary
                title={t('cart.sidebarTitle')}
                itemsLabel={t('cart.sidebarItems', { count: String(itemCount) })}
                items={sortedItems}
                formatCurrency={formatCurrency}
              />
              <CartTotalsSection
                totalQuantityLabel={t('cart.totalQuantity')}
                subtotalLabel={t('cart.subtotal')}
                totalLabel={t('customer.orders.total')}
                checkoutLabel={checkoutCtaLabel}
                checkoutBusyLabel={t('cart.checkoutBusy')}
                checkoutDisabled={busy || itemCount === 0}
                checkoutBusy={false}
                onCheckout={() => {
                  handleCheckout()
                }}
                itemCount={itemCount}
                subtotal={subtotal}
                formatCurrency={formatCurrency}
              />
            </aside>
          </div>
        )}
      </div>
    </div>
  )
}

export default CartPage
